import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { EyeOff, RotateCcw, Wand2, X } from "lucide-react";
import api, { BACKEND_URL } from "../lib/api";
import { extractError } from "../lib/formatError";

export default function AdminImageLibrary() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/models");
      setItems(data.items);
    } catch (e) { setError(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id, retire) => {
    try {
      await api.post(`/admin/models/${id}/${retire ? "retire" : "restore"}`);
      toast.success(retire ? "Retired" : "Restored");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div data-testid="admin-image-library">
      <Toaster theme="dark" position="top-center" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">Image library</div>
          <h1 className="font-display italic text-4xl text-[color:var(--ink)]">Models ({items.length})</h1>
        </div>
      </div>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((m) => (
          <div key={m.id} className={`border p-3 ${m.retired ? "opacity-50" : ""}`} style={{ borderColor: m.retired ? "var(--red)" : "var(--line)", background: "var(--card)" }} data-testid={`model-${m.id}`}>
            <div className="aspect-square bg-[color:var(--shell-2)] mb-3">
              <img src={`${BACKEND_URL}/api/assets/models/${m.dressed}`} alt={m.id} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-1">{m.ethnicity} · {m.hair}</div>
            <div className="text-xs text-[color:var(--ink)] truncate">{m.dress || m.dress_style}</div>
            <div className="text-[10px] text-[color:var(--muted)] truncate">{m.setting}</div>
            {m.retired && <div className="text-[10px] tracking-widest uppercase text-[color:var(--red)] mt-1">retired</div>}
            <div className="flex gap-1 mt-3">
              <button onClick={() => setPreview(m)} className="flex-1 py-1.5 border border-[color:var(--line)] text-[9px] tracking-widest uppercase text-[color:var(--muted)] hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]" data-testid={`model-view-${m.id}`}>View 4</button>
              <button onClick={() => toggle(m.id, !m.retired)} className={`flex-1 py-1.5 border text-[9px] tracking-widest uppercase ${m.retired ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--red)] text-[color:var(--red)]"}`} data-testid={`model-toggle-${m.id}`}>
                {m.retired ? "Restore" : "Retire"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {preview && <PreviewModal m={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function PreviewModal({ m, onClose }) {
  const reveals = m.reveals || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6" style={{ background: "rgba(6,4,3,0.9)", backdropFilter: "blur(4px)" }} data-testid="model-preview">
      <div className="w-full max-w-4xl border p-5" style={{ borderColor: "var(--line)", background: "var(--card)" }}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)]">Model preview</div>
            <div className="font-display italic text-2xl text-[color:var(--gold)]">{m.id}</div>
            <div className="text-xs text-[color:var(--muted)] mt-1">{m.prompt_summary || `${m.ethnicity} · ${m.hair} · ${m.setting}`}</div>
          </div>
          <button onClick={onClose} className="text-[color:var(--muted)] hover:text-[color:var(--ink)]"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <ImgTile label="Dressed" src={m.dressed} />
          {["red", "black", "gold"].map((c) => (
            <ImgTile key={c} label={`Reveal · ${c}`} src={reveals[c]} accent="var(--gold)" />
          ))}
        </div>
      </div>
    </div>
  );
}

function ImgTile({ label, src, accent }) {
  if (!src) return <div className="aspect-square border flex items-center justify-center text-[10px] tracking-widest uppercase text-[color:var(--muted)]" style={{ borderColor: "var(--line)" }}>Missing</div>;
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-1" style={{ color: accent || "var(--muted)" }}>{label}</div>
      <img src={`${BACKEND_URL}/api/assets/models/${src}`} alt={label} className="w-full aspect-square object-cover border" style={{ borderColor: "var(--line)" }} />
    </div>
  );
}
